// ProdutoModal.js - Modal para criar/editar produto (reutilizável)
// ✅ Estilo idêntico aos modais de Clientes/Serviços (produto-modal-*)
// ✅ Sincroniza fornecedor e preço de compra entre tabs
// ✅ Usa createPortal para aparecer SEMPRE por cima de qualquer modal
// ✅ Selects com seta única via .produto-select-wrapper
// ✅ CORRIGIDO: URLs sem 'api/' duplicado (o axios já tem baseURL '/api')
// ✅ CORRIGIDO: envia empresaId como query param
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Icon } from './SharedComponents';
import './ProdutoModal.css';

const API_BASE_URL = '/api';

// ==============================================
// CONSTANTES
// ==============================================
const UNIDADES_MEDIDA = [
    { value: 'UN', label: 'Unidade' },
    { value: 'KG', label: 'Quilograma' },
    { value: 'L', label: 'Litro' },
    { value: 'M', label: 'Metro' },
    { value: 'CX', label: 'Caixa' },
    { value: 'PCT', label: 'Pacote' }
];

const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.'
    },
    iva: null
};

// ==============================================
// COMPONENTE PRINCIPAL
// ==============================================
export default function ProdutoModal({
    onClose,
    onProdutoCriado,
    onProdutoEditado,
    produtoEdit,
    empresaId,
    isEdit = false
}) {
    // ==============================================
    // ESTADOS
    // ==============================================
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [categorias, setCategorias] = useState([]);
    const [fornecedores, setFornecedores] = useState([]);
    const [armazens, setArmazens] = useState([]);
    const [configEmpresa, setConfigEmpresa] = useState(CONFIG_PADRAO);
    const [activeTab, setActiveTab] = useState('geral');
    const [loggedUser, setLoggedUser] = useState(null);

    // ==============================================
    // FORM DATA
    // ==============================================
    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        codigo: '',
        precoUnitario: '',
        precoVenda: '',
        precoCompra: '',
        compraQuantidade: 0,
        stockAtual: 0,
        stockMinimo: 0,
        unidadeMedida: 'UN',
        categoriaId: '',
        fornecedorId: '',
        armazemId: '',
        ativo: true,
        impostos: {
            iva: null,
            ispc: 0,
            usarIvaEmpresa: true
        }
    });

    // ==============================================
    // STOCK FORM DATA (apenas para criação)
    // ==============================================
    const [stockFormData, setStockFormData] = useState({
        quantidade: 1,
        precoUnitario: 0,
        fornecedorId: '',
        notaFiscal: '',
        observacao: ''
    });

    // ==============================================
    // API INSTANCE
    // ==============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
        }
    });

    // ==============================================
    // FORMATAR MOEDA
    // ==============================================
    const formatarMoeda = (valor) => {
        const moeda = configEmpresa?.moeda || CONFIG_PADRAO.moeda;
        const { simbolo = 'MT', casasDecimais = 2, separadorDecimal = ',', separadorMilhar = '.' } = moeda;

        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        let numero = Number(valor);
        if (isNaN(numero)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        numero = numero.toFixed(casasDecimais);
        const partes = numero.split('.');
        let inteiros = partes[0];
        const decimais = partes[1] || '0'.repeat(casasDecimais);

        inteiros = inteiros.replace(/\B(?=(\d{3})+(?!\d))/g, separadorMilhar);

        return `${inteiros}${separadorDecimal}${decimais} ${simbolo}`;
    };

    // ==============================================
    // AUXILIARES
    // ==============================================
    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    const obterEmpresaId = () => {
        if (empresaId) return empresaId;
        try {
            const user = JSON.parse(localStorage.getItem('usuario') || '{}');
            return user.empresaId || user.empresa?._id || null;
        } catch (e) {
            console.error('❌ Erro ao obter empresaId:', e);
            return null;
        }
    };

    // ==============================================
    // CALCULAR PREÇO COM IVA
    // ==============================================
    const calcularPrecoComIVA = (precoSemIVA, iva) => {
        if (!precoSemIVA || precoSemIVA <= 0) return null;
        if (iva === null || iva === undefined || iva === 0) return precoSemIVA;
        const taxaIVA = Number(iva) / 100;
        return precoSemIVA * (1 + taxaIVA);
    };

    // ==============================================
    // CALCULAR PREÇO SEM IVA (INVERSO)
    // ==============================================
    const calcularPrecoSemIVA = (precoComIVA, iva) => {
        if (!precoComIVA || precoComIVA <= 0) return null;
        if (iva === null || iva === undefined || iva === 0) return precoComIVA;
        const taxaIVA = Number(iva) / 100;
        return precoComIVA / (1 + taxaIVA);
    };

    // ==============================================
    // IVA EFETIVO
    // ==============================================
    const getIvaEfetivo = (impostos) => {
        if (!impostos) return null;

        if (impostos.usarIvaEmpresa) {
            return configEmpresa?.iva !== null && configEmpresa?.iva !== undefined
                ? Number(configEmpresa.iva)
                : null;
        }

        return impostos.iva !== null && impostos.iva !== undefined
            ? Number(impostos.iva)
            : null;
    };

    // ==============================================
    // SANITIZAR PREÇO
    // ==============================================
    const sanitizarPreco = (valor) => {
        if (!valor) return '';
        let val = valor.replace(/[^0-9.]/g, '');
        const partes = val.split('.');
        if (partes.length > 2) {
            val = partes[0] + '.' + partes.slice(1).join('');
        }
        if (val.startsWith('.')) {
            val = '0' + val;
        }
        return val;
    };

    // ==============================================
    // BUSCAR CATEGORIAS
    // ==============================================
    const fetchCategorias = async () => {
        try {
            const response = await api.get('categorias');

            if (response.data && response.data.success) {
                const categoriasData = response.data.data || [];
                setCategorias(categoriasData);
                console.log(`✅ ${categoriasData.length} categorias carregadas`);
            } else if (Array.isArray(response.data)) {
                setCategorias(response.data);
                console.log(`✅ ${response.data.length} categorias carregadas`);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar categorias:', err);
            setCategorias([]);
        }
    };

    // ==============================================
    // BUSCAR FORNECEDORES
    // ==============================================
    const fetchFornecedores = async () => {
        try {
            const response = await api.get('fornecedores');

            if (response.data && response.data.success) {
                const fornecedoresData = response.data.data || [];
                setFornecedores(fornecedoresData);
                console.log(`✅ ${fornecedoresData.length} fornecedores carregados`);
            } else if (Array.isArray(response.data)) {
                setFornecedores(response.data);
                console.log(`✅ ${response.data.length} fornecedores carregados`);
            } else {
                setFornecedores([]);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar fornecedores:', err);
            setFornecedores([]);
        }
    };

    // ==============================================
    // BUSCAR ARMAZÉNS
    // ==============================================
    const fetchArmazens = async () => {
        try {
            const response = await api.get('armazens');

            if (response.data && response.data.success) {
                const armazensData = response.data.data || [];
                setArmazens(armazensData);
                console.log(`✅ ${armazensData.length} armazéns carregados`);
            } else if (Array.isArray(response.data)) {
                setArmazens(response.data);
                console.log(`✅ ${response.data.length} armazéns carregados`);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar armazéns:', err);
            setArmazens([]);
        }
    };

    // ==============================================
    // BUSCAR CONFIG EMPRESA (IVA)
    // ==============================================
    const fetchConfigEmpresa = async () => {
        try {
            const empresaIdParaBuscar = obterEmpresaId();

            console.log('📡 Buscando config da empresa. empresaId:', empresaIdParaBuscar);

            if (!empresaIdParaBuscar) {
                console.warn('⚠️ empresaId não encontrado, usando config padrão');
                setConfigEmpresa(CONFIG_PADRAO);
                return;
            }

            // ✅ URL SEM 'api/' duplicado
            try {
                const url = `produtos?limit=1&empresaId=${empresaIdParaBuscar}`;
                const response = await api.get(url);
                if (response.data && response.data.configuracao) {
                    const config = response.data.configuracao;
                    setConfigEmpresa(prev => ({
                        ...prev,
                        iva: config.iva !== null && config.iva !== undefined ? Number(config.iva) : null,
                        moeda: config.moeda || CONFIG_PADRAO.moeda,
                        regimeIVA: config.regimeIVA || 'Normal'
                    }));
                    console.log('✅ Config carregada via /produtos:', config);
                    return;
                }
            } catch (err) {
                console.warn('⚠️ Fallback via /produtos falhou:', err.message);
            }

            // Fallback via /servicos
            try {
                const url2 = `servicos?limit=1&empresaId=${empresaIdParaBuscar}`;
                const response2 = await api.get(url2);
                if (response2.data && response2.data.configuracao) {
                    const config = response2.data.configuracao;
                    setConfigEmpresa(prev => ({
                        ...prev,
                        iva: config.iva !== null && config.iva !== undefined ? Number(config.iva) : null,
                        moeda: config.moeda || CONFIG_PADRAO.moeda,
                        regimeIVA: config.regimeIVA || 'Normal'
                    }));
                    console.log('✅ Config carregada via /servicos:', config);
                    return;
                }
            } catch (err) {
                console.warn('⚠️ Fallback via /servicos falhou:', err.message);
            }

            console.warn('⚠️ Configuração não encontrada, usando padrão');
            setConfigEmpresa(CONFIG_PADRAO);

        } catch (error) {
            console.error('❌ Erro ao buscar configuração da empresa:', error);
            setConfigEmpresa(CONFIG_PADRAO);
        }
    };

    // ==============================================
    // BUSCAR USUÁRIO LOGADO
    // ==============================================
    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    setLoggedUser(storedUser);
                }
                return;
            }

            const response = await axios.get(`${API_BASE_URL}users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                setLoggedUser(response.data.data);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador:', err);
        }
    };

    // ==============================================
    // CARREGAR DADOS INICIAIS
    // ==============================================
    useEffect(() => {
        console.log('📦 ProdutoModal montado, carregando dados...');
        fetchLoggedUser();
        fetchCategorias();
        fetchFornecedores();
        fetchArmazens();
        fetchConfigEmpresa();
        
    }, []);

    // ==============================================
    // CARREGAR DADOS PARA EDIÇÃO
    // ==============================================
    useEffect(() => {
        if (isEdit && produtoEdit) {
            console.log('📝 Carregando produto para edição:', produtoEdit.nome);

            let armazemId = '';
            if (produtoEdit.armazemId) {
                if (typeof produtoEdit.armazemId === 'object' && produtoEdit.armazemId._id) {
                    armazemId = produtoEdit.armazemId._id;
                } else if (typeof produtoEdit.armazemId === 'string') {
                    armazemId = produtoEdit.armazemId;
                }
            }

            let fornecedorId = '';
            if (produtoEdit.fornecedorId) {
                if (typeof produtoEdit.fornecedorId === 'object' && produtoEdit.fornecedorId._id) {
                    fornecedorId = produtoEdit.fornecedorId._id;
                } else if (typeof produtoEdit.fornecedorId === 'string') {
                    fornecedorId = produtoEdit.fornecedorId;
                }
            }

            const ivaEfetivo = getIvaEfetivo(produtoEdit.impostos);
            const precoVenda = parseFloat(produtoEdit.precoVenda) || 0;
            let precoUnitario = precoVenda;

            if (ivaEfetivo !== null && ivaEfetivo !== undefined && precoVenda > 0) {
                const precoSemIva = calcularPrecoSemIVA(precoVenda, ivaEfetivo);
                if (precoSemIva !== null) {
                    precoUnitario = precoSemIva;
                }
            }

            setFormData({
                nome: produtoEdit.nome || '',
                descricao: produtoEdit.descricao || '',
                codigo: produtoEdit.codigo || '',
                precoUnitario: precoUnitario > 0 ? Math.round(precoUnitario * 100) / 100 : '',
                precoVenda: produtoEdit.precoVenda || '',
                precoCompra: produtoEdit.precoCompra || '',
                compraQuantidade: produtoEdit.compraQuantidade || 0,
                stockAtual: produtoEdit.stockAtual || 0,
                stockMinimo: produtoEdit.stockMinimo || 0,
                unidadeMedida: produtoEdit.unidadeMedida || 'UN',
                categoriaId: produtoEdit.categoriaId?._id || produtoEdit.categoriaId || '',
                fornecedorId: fornecedorId,
                armazemId: armazemId,
                ativo: produtoEdit.ativo !== undefined ? produtoEdit.ativo : true,
                impostos: {
                    iva: produtoEdit.impostos?.iva !== null && produtoEdit.impostos?.iva !== undefined ? produtoEdit.impostos.iva : null,
                    ispc: produtoEdit.impostos?.ispc || 0,
                    usarIvaEmpresa: produtoEdit.impostos?.usarIvaEmpresa !== undefined
                        ? produtoEdit.impostos.usarIvaEmpresa
                        : true
                }
            });

            if (produtoEdit.precoCompra) {
                setStockFormData(prev => ({
                    ...prev,
                    precoUnitario: parseFloat(produtoEdit.precoCompra) || 0
                }));
            }
            if (fornecedorId) {
                setStockFormData(prev => ({
                    ...prev,
                    fornecedorId: fornecedorId
                }));
            }
        }
    }, [isEdit, produtoEdit]);

    // ==============================================
    // EFECTO PARA CALCULAR PREÇO DE VENDA
    // ==============================================
    const isUpdatingPrice = useRef(false);

    useEffect(() => {
        if (isUpdatingPrice.current) return;

        const precoUnitario = parseFloat(formData.precoUnitario) || 0;
        const ivaEfetivo = getIvaEfetivo(formData.impostos);

        if (precoUnitario > 0 && ivaEfetivo !== null && ivaEfetivo !== undefined) {
            const precoComIva = calcularPrecoComIVA(precoUnitario, ivaEfetivo);

            if (precoComIva !== null) {
                isUpdatingPrice.current = true;
                setFormData(prev => ({
                    ...prev,
                    precoVenda: Math.round(precoComIva * 100) / 100
                }));
                setTimeout(() => {
                    isUpdatingPrice.current = false;
                }, 100);
            }
        } else if (precoUnitario > 0 && (ivaEfetivo === null || ivaEfetivo === undefined)) {
            isUpdatingPrice.current = true;
            setFormData(prev => ({
                ...prev,
                precoVenda: precoUnitario
            }));
            setTimeout(() => {
                isUpdatingPrice.current = false;
            }, 100);
        }
    }, [formData.precoUnitario, formData.impostos.iva, formData.impostos.usarIvaEmpresa, configEmpresa?.iva]);

    // ==============================================
    // SINCRONIZAR FORNECEDOR
    // ==============================================
    useEffect(() => {
        if (formData.fornecedorId) {
            setStockFormData(prev => ({
                ...prev,
                fornecedorId: formData.fornecedorId
            }));
        }
    }, [formData.fornecedorId]);

    // ==============================================
    // SINCRONIZAR PRECO DE COMPRA
    // ==============================================
    useEffect(() => {
        const precoCompra = parseFloat(formData.precoCompra) || 0;
        if (precoCompra > 0) {
            setStockFormData(prev => ({
                ...prev,
                precoUnitario: precoCompra
            }));
        }
    }, [formData.precoCompra]);

    // ==============================================
    // HANDLE CHANGE
    // ==============================================
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.startsWith('impostos.')) {
            const field = name.split('.')[1];

            if (field === 'iva') {
                const val = value === '' ? null : value;
                setFormData(prev => ({
                    ...prev,
                    impostos: { ...prev.impostos, [field]: val }
                }));
            } else if (field === 'usarIvaEmpresa') {
                setFormData(prev => ({
                    ...prev,
                    impostos: {
                        ...prev.impostos,
                        [field]: checked,
                        iva: checked ? null : prev.impostos.iva
                    }
                }));
            } else {
                const val = type === 'checkbox' ? checked : value;
                setFormData(prev => ({
                    ...prev,
                    impostos: { ...prev.impostos, [field]: val }
                }));
            }
        } else if (name === 'precoUnitario') {
            const val = sanitizarPreco(value);
            setFormData(prev => ({ ...prev, [name]: val }));
        } else if (name === 'precoVenda') {
            const val = sanitizarPreco(value);
            setFormData(prev => ({ ...prev, [name]: val }));
        } else if (name === 'precoCompra') {
            const val = sanitizarPreco(value);
            setFormData(prev => ({ ...prev, [name]: val }));
            const precoVal = parseFloat(val) || 0;
            if (precoVal > 0) {
                setStockFormData(prev => ({
                    ...prev,
                    precoUnitario: precoVal
                }));
            }
        } else {
            const val = type === 'checkbox' ? checked : value;
            setFormData(prev => ({ ...prev, [name]: val }));
        }
    };

    // ==============================================
    // HANDLE CHANGE DO STOCK
    // ==============================================
    const handleStockChange = (e) => {
        const { name, value } = e.target;
        setStockFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'fornecedorId') {
            setFormData(prev => ({
                ...prev,
                fornecedorId: value
            }));
        }

        if (name === 'precoUnitario') {
            const precoVal = parseFloat(value) || 0;
            if (precoVal > 0) {
                setFormData(prev => ({
                    ...prev,
                    precoCompra: precoVal.toString()
                }));
            }
        }

        if (name === 'quantidade') {
            setFormData(prev => ({
                ...prev,
                stockAtual: parseInt(value) || 0,
                compraQuantidade: parseInt(value) || 0
            }));
        }
    };

    // ==============================================
    // RESET FORM
    // ==============================================
    const resetForm = () => {
        setFormData({
            nome: '',
            descricao: '',
            codigo: '',
            precoUnitario: '',
            precoVenda: '',
            precoCompra: '',
            compraQuantidade: 0,
            stockAtual: 0,
            stockMinimo: 0,
            unidadeMedida: 'UN',
            categoriaId: '',
            fornecedorId: '',
            armazemId: '',
            ativo: true,
            impostos: {
                iva: null,
                ispc: 0,
                usarIvaEmpresa: true
            }
        });
        setStockFormData({
            quantidade: 1,
            precoUnitario: 0,
            fornecedorId: '',
            notaFiscal: '',
            observacao: ''
        });
        setMessage({ text: '', type: '' });
        setActiveTab('geral');
    };

    // ==============================================
    // SUBMIT - CRIAR
    // ==============================================
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: '', type: '' });

        if (!formData.nome.trim()) {
            setMessage({ text: 'O nome do produto é obrigatório', type: 'danger' });
            setLoading(false);
            return;
        }

        if (!formData.precoVenda || parseFloat(formData.precoVenda) <= 0) {
            setMessage({ text: 'Preço de venda inválido', type: 'danger' });
            setLoading(false);
            return;
        }

        const empresaParaEnviar = obterEmpresaId();
        if (!empresaParaEnviar) {
            setMessage({
                text: 'Utilizador sem empresa associada. Contacte o administrador.',
                type: 'danger'
            });
            setLoading(false);
            return;
        }

        const fornecedorId = stockFormData.fornecedorId || formData.fornecedorId || null;
        const stockQuantidade = parseInt(stockFormData.quantidade) || 0;
        const stockPreco = parseFloat(stockFormData.precoUnitario) || 0;

        let quantidadeFinal = 0;
        let precoFinal = 0;
        let historicoEntradas = [];

        if (stockQuantidade > 0 && stockPreco > 0) {
            quantidadeFinal = stockQuantidade;
            precoFinal = stockPreco;
            historicoEntradas = [{
                quantidade: stockQuantidade,
                precoUnitario: stockPreco,
                fornecedorId: fornecedorId,
                dataEntrada: new Date().toISOString(),
                observacao: stockFormData.observacao || 'Entrada inicial na criação do produto',
                criadoPor: loggedUser?._id || loggedUser?.id || null,
                notaFiscal: stockFormData.notaFiscal || null
            }];
        }

        const dadosParaEnviar = {
            nome: formData.nome.trim(),
            descricao: formData.descricao?.trim() || '',
            codigo: formData.codigo?.trim() || '',
            precoVenda: parseFloat(formData.precoVenda) || 0,
            precoCompra: precoFinal,
            compraQuantidade: quantidadeFinal,
            stockAtual: quantidadeFinal,
            stockMinimo: parseInt(formData.stockMinimo) || 0,
            unidadeMedida: formData.unidadeMedida || 'UN',
            categoriaId: formData.categoriaId || null,
            fornecedorId: fornecedorId,
            armazemId: formData.armazemId || null,
            empresaId: empresaParaEnviar,
            ativo: formData.ativo !== undefined ? formData.ativo : true,
            impostos: {
                iva: formData.impostos.iva !== '' && formData.impostos.iva !== null
                    ? parseFloat(formData.impostos.iva)
                    : null,
                ispc: parseFloat(formData.impostos.ispc) || 0,
                usarIvaEmpresa: formData.impostos.usarIvaEmpresa
            },
            historicoEntradas: historicoEntradas
        };

        try {
            console.log('📤 Criando produto:', dadosParaEnviar);
            const response = await api.post('produtos', dadosParaEnviar);

            if (response.data) {
                showMessage('Produto criado com sucesso!', 'success');

                if (onProdutoCriado) {
                    onProdutoCriado(response.data.data || response.data);
                }

                setTimeout(() => {
                    resetForm();
                    onClose();
                }, 1000);
            }
        } catch (err) {
            console.error('❌ Erro ao criar produto:', err);
            setMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao criar produto',
                type: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // SUBMIT - EDIÇÃO
    // ==============================================
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ text: '', type: '' });

        if (!formData.nome.trim()) {
            setMessage({ text: 'O nome do produto é obrigatório', type: 'danger' });
            setLoading(false);
            return;
        }

        if (!formData.precoVenda || parseFloat(formData.precoVenda) <= 0) {
            setMessage({ text: 'Preço de venda inválido', type: 'danger' });
            setLoading(false);
            return;
        }

        try {
            const { _id, ...updateData } = formData;

            if (updateData.stockAtual !== undefined && produtoEdit) {
                const diff = updateData.stockAtual - (produtoEdit.stockAtual || 0);
                if (diff > 0) {
                    if (!updateData.historicoEntradas) {
                        updateData.historicoEntradas = [];
                    }
                    const fornecedorId = updateData.fornecedorId || null;
                    updateData.historicoEntradas.push({
                        quantidade: diff,
                        precoUnitario: parseFloat(updateData.precoCompra) || 0,
                        fornecedorId: fornecedorId,
                        dataEntrada: new Date().toISOString(),
                        observacao: `Ajuste de stock via edição (+${diff} unidades)`,
                        criadoPor: loggedUser?._id || loggedUser?.id || null
                    });
                }
            }

            console.log('📤 Atualizando produto:', updateData);
            const response = await api.put(`produtos/${produtoEdit._id}`, updateData);

            if (response.data) {
                showMessage('Produto atualizado com sucesso!', 'success');

                if (onProdutoEditado) {
                    onProdutoEditado(response.data.data || response.data);
                }

                setTimeout(() => {
                    resetForm();
                    onClose();
                }, 1000);
            }
        } catch (err) {
            console.error('❌ Erro ao atualizar produto:', err);
            setMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao atualizar produto',
                type: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // RENDER
    // ==============================================
    const isEditMode = isEdit && produtoEdit;

    const precoUnitarioAtual = parseFloat(formData.precoUnitario) || 0;
    const ivaEfetivo = getIvaEfetivo(formData.impostos);
    const precoVendaCalculado = calcularPrecoComIVA(precoUnitarioAtual, ivaEfetivo);

    // ✅ RENDERIZA VIA PORTAL
    return createPortal(
        <div className="produto-modal-overlay" onClick={onClose}>
            <div className="produto-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="produto-modal-header">
                    <h3>
                        <Icon name={isEditMode ? "edit" : "add_box"} size={22} />
                        {isEditMode ? 'Editar Produto' : 'Novo Produto'}
                    </h3>
                    <button className="produto-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="produto-modal-body">
                    {message.text && (
                        <div className={`produto-modal-alert produto-modal-alert-${message.type}`}>
                            <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={18} />
                            <span>{message.text}</span>
                            <button className="produto-alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                        </div>
                    )}

                    <form onSubmit={isEditMode ? handleEditSubmit : handleCreateSubmit}>
                        {/* TABS */}
                        <div className="produto-modal-tabs">
                            <button
                                type="button"
                                className={`produto-modal-tab-btn ${activeTab === 'geral' ? 'active' : ''}`}
                                onClick={() => setActiveTab('geral')}
                            >
                                <Icon name="info" size={16} />
                                <span className="tab-label">Dados Gerais</span>
                            </button>
                            <button
                                type="button"
                                className={`produto-modal-tab-btn ${activeTab === 'impostos' ? 'active' : ''}`}
                                onClick={() => setActiveTab('impostos')}
                            >
                                <Icon name="receipt" size={16} />
                                <span className="tab-label">Impostos</span>
                            </button>
                            {!isEditMode && (
                                <button
                                    type="button"
                                    className={`produto-modal-tab-btn ${activeTab === 'stock' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('stock')}
                                >
                                    <Icon name="inventory_2" size={16} />
                                    <span className="tab-label">Entrada de Stock</span>
                                </button>
                            )}
                        </div>

                        {/* TAB DADOS GERAIS */}
                        <div className={`produto-modal-tab-panel ${activeTab === 'geral' ? 'active' : ''}`}>
                            <div className="produto-modal-section">
                                <div className="produto-form-group">
                                    <label>Nome <span className="produto-required">*</span></label>
                                    <input
                                        type="text"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        required
                                        placeholder="Nome do produto"
                                    />
                                </div>
                                <div className="produto-form-row">
                                    <div className="produto-form-group">
                                        <label>Código</label>
                                        <input
                                            type="text"
                                            name="codigo"
                                            value={formData.codigo}
                                            onChange={handleChange}
                                            placeholder="Código (opcional)"
                                            disabled={isEditMode}
                                        />
                                    </div>
                                    <div className="produto-form-group">
                                        <label>Categoria</label>
                                        <div className="produto-select-wrapper">
                                            <select name="categoriaId" value={formData.categoriaId} onChange={handleChange}>
                                                <option value="">Selecione uma categoria</option>
                                                {categorias.map(cat => (
                                                    <option key={cat._id} value={cat._id}>{cat.nome}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="produto-form-row">
                                    <div className="produto-form-group">
                                        <label>Armazém</label>
                                        <div className="produto-select-wrapper">
                                            <select name="armazemId" value={formData.armazemId} onChange={handleChange}>
                                                <option value="">Selecione um armazém</option>
                                                {armazens.map(arm => (
                                                    <option key={arm._id} value={arm._id}>
                                                        {arm.nome} {arm.codigo ? `(${arm.codigo})` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <small className="produto-form-help">Armazém onde o produto será armazenado</small>
                                    </div>
                                    <div className="produto-form-group">
                                        <label>Preço Unitário <span className="produto-required">*</span></label>
                                        <input
                                            type="text"
                                            name="precoUnitario"
                                            value={formData.precoUnitario}
                                            onChange={handleChange}
                                            required
                                            placeholder="0.00"
                                            inputMode="decimal"
                                            autoComplete="off"
                                        />
                                        <small className="produto-form-help">Preço base sem IVA</small>
                                    </div>
                                </div>
                                <div className="produto-form-row">
                                    <div className="produto-form-group">
                                        <label>Preço de Venda (com IVA)</label>
                                        <input
                                            type="text"
                                            name="precoVenda"
                                            value={formData.precoVenda}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            readOnly
                                        />
                                        {ivaEfetivo !== null && ivaEfetivo !== undefined && precoUnitarioAtual > 0 && (
                                            <small className="produto-form-help" style={{ color: 'var(--color-info)' }}>
                                                <Icon name="info" size={12} />
                                                Calculado com IVA de {ivaEfetivo}% (base: {formatarMoeda(precoUnitarioAtual)})
                                            </small>
                                        )}
                                        {ivaEfetivo === null && precoUnitarioAtual > 0 && (
                                            <small className="produto-form-help" style={{ color: 'var(--color-warning)' }}>
                                                <Icon name="warning" size={12} />
                                                Sem IVA configurado
                                            </small>
                                        )}
                                    </div>
                                    <div className="produto-form-group">
                                        <label>Preço de Compra</label>
                                        <input
                                            type="text"
                                            name="precoCompra"
                                            value={formData.precoCompra}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            inputMode="decimal"
                                            autoComplete="off"
                                        />
                                        <small className="produto-form-help" style={{ color: 'var(--primary)' }}>
                                            🔄 Será copiado para o Preço Unitário da aba "Entrada de Stock"
                                        </small>
                                    </div>
                                </div>
                                <div className="produto-form-row">
                                    <div className="produto-form-group">
                                        <label>Stock Mínimo</label>
                                        <input
                                            type="number"
                                            name="stockMinimo"
                                            value={formData.stockMinimo}
                                            onChange={handleChange}
                                            placeholder="0"
                                            min="0"
                                        />
                                    </div>
                                    <div className="produto-form-group">
                                        <label>Unidade de Medida</label>
                                        <div className="produto-select-wrapper">
                                            <select name="unidadeMedida" value={formData.unidadeMedida} onChange={handleChange}>
                                                {UNIDADES_MEDIDA.map(u => (
                                                    <option key={u.value} value={u.value}>{u.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="produto-form-row">
                                    <div className="produto-form-group">
                                        <label>Fornecedor</label>
                                        <div className="produto-select-wrapper">
                                            <select
                                                name="fornecedorId"
                                                value={formData.fornecedorId || ''}
                                                onChange={handleChange}
                                            >
                                                <option value="">Selecione um fornecedor</option>
                                                {fornecedores.map(f => (
                                                    <option key={f._id} value={f._id}>{f.nome}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <small className="produto-form-help" style={{ color: 'var(--primary)' }}>
                                            🔄 Será copiado para a aba "Entrada de Stock"
                                        </small>
                                    </div>
                                    <div className="produto-form-group">
                                        {/* Espaço vazio */}
                                    </div>
                                </div>
                                <div className="produto-form-group">
                                    <label>Descrição</label>
                                    <textarea
                                        name="descricao"
                                        value={formData.descricao}
                                        onChange={handleChange}
                                        placeholder="Descrição do produto"
                                        rows="2"
                                    />
                                </div>
                                <div className="produto-form-group">
                                    <label className="produto-checkbox-label">
                                        <input type="checkbox" name="ativo" checked={formData.ativo} onChange={handleChange} />
                                        Produto Ativo
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* TAB IMPOSTOS */}
                        <div className={`produto-modal-tab-panel ${activeTab === 'impostos' ? 'active' : ''}`}>
                            <div className="produto-modal-section">
                                <div className="produto-form-row">
                                    <div className="produto-form-group">
                                        <label>IVA (%)</label>
                                        <input
                                            type="number"
                                            name="impostos.iva"
                                            value={formData.impostos.iva !== null && formData.impostos.iva !== undefined ? formData.impostos.iva : ''}
                                            onChange={handleChange}
                                            placeholder="Deixar em branco para usar o da empresa"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            disabled={formData.impostos.usarIvaEmpresa}
                                        />
                                        <small className="produto-form-help">
                                            {formData.impostos.usarIvaEmpresa
                                                ? `Usando IVA da empresa (${configEmpresa?.iva !== null && configEmpresa?.iva !== undefined ? `${configEmpresa.iva}%` : 'N/D'})`
                                                : formData.impostos.iva !== null ? `IVA específico: ${formData.impostos.iva}%` : 'IVA específico do produto'}
                                        </small>
                                    </div>
                                    <div className="produto-form-group">
                                        <label>ISPC (%)</label>
                                        <input
                                            type="number"
                                            name="impostos.ispc"
                                            value={formData.impostos.ispc || 0}
                                            onChange={handleChange}
                                            placeholder="0"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                        />
                                    </div>
                                </div>
                                <div className="produto-form-group">
                                    <label className="produto-checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="impostos.usarIvaEmpresa"
                                            checked={formData.impostos.usarIvaEmpresa}
                                            onChange={handleChange}
                                        />
                                        Usar IVA da empresa ({configEmpresa?.iva !== null && configEmpresa?.iva !== undefined ? `${configEmpresa.iva}%` : 'N/D'})
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* TAB ENTRADA DE STOCK */}
                        {!isEditMode && (
                            <div className={`produto-modal-tab-panel ${activeTab === 'stock' ? 'active' : ''}`}>
                                <div className="produto-modal-section">
                                    <div className="produto-stock-info-box">
                                        <p>
                                            <Icon name="info" size={16} />
                                            Os campos <strong>Preço Unitário</strong> e <strong>Fornecedor</strong> são sincronizados automaticamente
                                            com os dados da aba <strong>Dados Gerais</strong>.
                                        </p>
                                    </div>

                                    <div className="produto-form-row">
                                        <div className="produto-form-group">
                                            <label>Quantidade <span className="produto-required">*</span></label>
                                            <input
                                                type="number"
                                                name="quantidade"
                                                value={stockFormData.quantidade}
                                                onChange={handleStockChange}
                                                min="1"
                                                step="1"
                                                placeholder="1"
                                            />
                                            <small className="produto-form-help">Quantidade a adicionar ao stock</small>
                                        </div>
                                        <div className="produto-form-group">
                                            <label>Preço Unitário <span className="produto-required">*</span></label>
                                            <input
                                                type="number"
                                                name="precoUnitario"
                                                value={stockFormData.precoUnitario}
                                                onChange={handleStockChange}
                                                min="0.01"
                                                step="0.01"
                                                placeholder="0.00"
                                            />
                                            <small className="produto-form-help" style={{ color: 'var(--primary)' }}>
                                                🔄 Sincronizado com o Preço de Compra da aba Dados Gerais
                                            </small>
                                        </div>
                                    </div>

                                    <div className="produto-form-row">
                                        <div className="produto-form-group">
                                            <label>Fornecedor</label>
                                            <div className="produto-select-wrapper">
                                                <select
                                                    name="fornecedorId"
                                                    value={stockFormData.fornecedorId || ''}
                                                    onChange={handleStockChange}
                                                >
                                                    <option value="">Selecione um fornecedor</option>
                                                    {fornecedores.map(f => (
                                                        <option key={f._id} value={f._id}>{f.nome}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <small className="produto-form-help" style={{ color: 'var(--primary)' }}>
                                                🔄 Sincronizado com o Fornecedor da aba Dados Gerais
                                            </small>
                                        </div>
                                        <div className="produto-form-group">
                                            <label>Nº Nota Fiscal</label>
                                            <input
                                                type="text"
                                                name="notaFiscal"
                                                value={stockFormData.notaFiscal || ''}
                                                onChange={handleStockChange}
                                                placeholder="Número da nota fiscal (opcional)"
                                            />
                                        </div>
                                    </div>

                                    <div className="produto-form-group">
                                        <label>Observação</label>
                                        <textarea
                                            name="observacao"
                                            value={stockFormData.observacao || ''}
                                            onChange={handleStockChange}
                                            placeholder="Observações adicionais (opcional)"
                                            rows="2"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* AÇÕES */}
                        <div className="produto-modal-actions">
                            <button type="submit" className="produto-modal-btn-primary" disabled={loading}>
                                {loading ? (isEditMode ? 'A atualizar...' : 'A criar...') : (isEditMode ? 'Salvar Alterações' : 'Criar Produto')}
                            </button>
                            <button type="button" className="produto-modal-btn-secondary" onClick={onClose}>
                                Cancelar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
}